import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { VideoPlayer } from "@/components/VideoPlayer";
import { CONTACT, HERO_VIDEO, IMAGES } from "@/data/content";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { scrollToHash } from "@/components/Navbar";

const CHIPS = ["RESIDENTIAL", "COMMERCIAL", "INDUSTRIAL", "MEGA PROJECTS"];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

    return (
        <section id="home" ref={ref} data-testid="hero-section"
            className="relative flex min-h-[100svh] items-center overflow-hidden bg-omsivo-deep">
            <motion.div style={{ scale: videoScale }} className="absolute inset-0">
                <VideoPlayer
                    src={HERO_VIDEO.src}
                    srcMobile={HERO_VIDEO.srcMobile}
                    poster={HERO_VIDEO.poster}
                    fallbackImg={IMAGES.heroFallback}
                    className="h-full w-full"
                    testid="hero-video"
                    label="Conceptual night construction footage"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-omsivo-deep/95 via-omsivo-deep/60 to-omsivo-deep/95" />
            <div className="absolute inset-0 bg-gradient-to-r from-omsivo-deep/85 via-omsivo-deep/35 to-transparent" />
            <div className="absolute inset-0 opacity-60" aria-hidden
                style={{
                    backgroundImage: "linear-gradient(rgba(96,158,255,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(96,158,255,0.07) 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                }} />

            <div className="relative z-10 mx-auto w-full max-w-[1440px] px-4 pb-32 pt-32 sm:px-6 lg:px-10 lg:pb-36 lg:pt-40">
                <motion.div style={{ y: textY }} className="max-w-4xl">
                    <Reveal y={16}>
                        <p className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.3em] text-white/70 backdrop-blur" data-testid="hero-overline">
                            <span className="h-1.5 w-1.5 rounded-full bg-flame" />
                            Construction · Engineering · Development
                        </p>
                    </Reveal>

                    <MaskedLines
                        className="mt-8 font-display font-black uppercase leading-[0.98] tracking-tight"
                        lineClassName="text-[12.5vw] text-white sm:text-6xl lg:text-7xl xl:text-[5.4rem]"
                        lines={[
                            <>NOT JUST <span className="text-white/40">HOUSES.</span></>,
                            <>WE BUILD <span className="text-flame">CONCEPTS.</span></>,
                        ]}
                    />

                    <Reveal delay={0.55}>
                        <p className="mt-7 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg" data-testid="hero-subheadline">
                            FROM YOUR FIRST IDEA TO YOUR FINAL KEY.
                        </p>
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.32em] text-flame" data-testid="hero-positioning">
                            From homes to mega projects
                        </p>
                    </Reveal>

                    <Reveal delay={0.7}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <a href="#build" data-testid="hero-explore-btn"
                                onClick={(e) => { e.preventDefault(); scrollToHash("#build"); }}
                                className="ping-soft relative rounded-full bg-flame px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.1em] text-white shadow-xl shadow-flame/30 transition-transform hover:-translate-y-0.5">
                                Explore OMSIVO
                            </a>
                            <a href="#contact" data-testid="hero-discuss-btn"
                                onClick={(e) => { e.preventDefault(); trackEvent(EVENTS.estimateClick, { location: "hero" }); scrollToHash("#contact"); }}
                                className="rounded-full border border-white/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors hover:border-flame hover:text-flame">
                                Discuss Your Project
                            </a>
                            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
                                onClick={() => trackEvent(EVENTS.whatsappClick, { location: "hero" })}
                                className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-white">
                                <MessageCircle className="h-4 w-4 text-flame" /> Chat on WhatsApp
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.85}>
                        <div className="mt-12 flex flex-wrap gap-2" data-testid="hero-sector-chips">
                            {CHIPS.map((c) => (
                                <span key={c} className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/55 backdrop-blur">
                                    {c}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                </motion.div>
            </div>

            <p className="absolute bottom-6 right-4 z-10 hidden font-mono text-[8px] uppercase tracking-[0.24em] text-white/35 sm:right-24 sm:block" data-testid="hero-footage-note">
                Conceptual footage · Not a completed OMSIVO project
            </p>
            <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
                <a href="#build" data-testid="hero-scroll-cue" onClick={(e) => { e.preventDefault(); scrollToHash("#build"); }}
                    className="flex flex-col items-center gap-2 text-white/45 transition-colors hover:text-white">
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em]">Scroll</span>
                    <ArrowDown className="h-4 w-4 animate-bounce" />
                </a>
            </div>
        </section>
    );
};
